"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createBooksPrintableGroups = void 0;
const uuid_1 = require("uuid");
const lodash_clonedeep_1 = __importDefault(require("lodash.clonedeep"));
const either_1 = require("../../types/either");
const Template_1 = require("../../models/Template");
const createBooksCoverPrintable_1 = __importDefault(require("./createBooksCoverPrintable"));
const createBooksInteriorPrintable_1 = __importDefault(require("./createBooksInteriorPrintable"));
const project_1 = require("../project");
const createBooksBoxPrintable_1 = __importDefault(require("./createBooksBoxPrintable"));
const constants_1 = require("../../services/designs/constants");
const designs_1 = require("../designs");
const constants_2 = require("../../utils/constants");
/**
 * This function does 2 things
 * 1. For the creation of a new book project (currentPrintableGroups not provided)
 *     It will create and return cover & interior printable groups
 * 2. For an existing book project (currentPrintableGroups provided)
 *     It will add additional pages to the interior printable group if the min page count is greater than the current page count
 * @param userId
 * @param projectId
 * @param creationDate
 * @param template
 * @param options
 * @param designId
 * @param currentPrintableGroups
 * @param mobileUserAgentCheck
 * @param hasDefaultDesign

 * @returns
 */
const createBooksPrintableGroups = _ref => {
  let {
    userId,
    projectId,
    creationDate,
    template,
    hammerCategory,
    currentPrintableGroups,
    minimumPageCountOverride,
    hasBoxPrintableGroup,
    mobileUserAgentCheck,
    hasDefaultDesign,
    userCanAddText,
    sku
  } = _ref;
  var _a;
  if (!(0, Template_1.isTemplateOriginal)(template)) {
    throw new Error('Error when creating book printable groups: Unsupported template schema version');
  }
  let coverGroup = (0, lodash_clonedeep_1.default)(currentPrintableGroups === null || currentPrintableGroups === void 0 ? void 0 : currentPrintableGroups.find(pg => pg.type === 'cover'));
  let interiorGroup = (0, lodash_clonedeep_1.default)(currentPrintableGroups === null || currentPrintableGroups === void 0 ? void 0 : currentPrintableGroups.find(pg => pg.type === 'interior'));
  let woodenBoxGroup = hasBoxPrintableGroup ? (0, lodash_clonedeep_1.default)(currentPrintableGroups === null || currentPrintableGroups === void 0 ? void 0 : currentPrintableGroups.find(pg => pg.type === 'wooden-box')) : undefined;
  if (currentPrintableGroups && !(coverGroup && interiorGroup)) {
    return (0, either_1.left)(new Error("Hmm these printable groups don't look like they're part of a book project..."));
  }
  const interiorPrintableGroupId = (0, uuid_1.v5)(`Time ${creationDate} Printable Group interior`, projectId);
  if (!interiorGroup) {
    interiorGroup = {
      id: interiorPrintableGroupId,
      type: 'interior',
      qty: 1,
      printables: []
    };
  }
  const currentNumPrintables = ((_a = interiorGroup === null || interiorGroup === void 0 ? void 0 : interiorGroup.printables) === null || _a === void 0 ? void 0 : _a.length) || 0;
  const minimumPageCount = minimumPageCountOverride ? minimumPageCountOverride : template.pages.min;
  const numPrintablesNeeded = minimumPageCount - currentNumPrintables;
  for (let i = 0; i < numPrintablesNeeded; i++) {
    const printableId = (0, uuid_1.v5)(`Time ${creationDate} Printable interior ${i}`, interiorPrintableGroupId);
    const designFilters = [];
    if (minimumPageCountOverride) {
      const pageCountFilter = d => (0, designs_1.isDesignAllowedForPageCount)(d, minimumPageCountOverride);
      designFilters.unshift(pageCountFilter);
    }
    if (userCanAddText) {
      designFilters.push(constants_1.NO_TEXT_FILTER);
    }
    if (mobileUserAgentCheck && !hasDefaultDesign) {
      // default mobileUserAgentCheck design selection should have one image and no text per EN-5790
      designFilters.push(constants_1.MOBILE_FILTER);
    } else {
      designFilters.push(constants_1.DEFAULT_FILTER);
    }
    const isHardcover = constants_2.HARDCOVER_SKUS_ALL_LIST.includes(sku);
    if (isHardcover) {
      const isFirstPage = !interiorGroup.printables.length && i === 0;
      const designatedDesignFilter = getHardcoverDesignFilter(template, isFirstPage);
      designatedDesignFilter && designFilters.push(designatedDesignFilter);
    }
    const createBooksInteriorPrintableResult = (0, createBooksInteriorPrintable_1.default)({
      userId,
      projectId,
      creationDate,
      template,
      interiorPrintableIdx: currentNumPrintables + i,
      printableId,
      designFilters
    });
    if (createBooksInteriorPrintableResult.isLeft()) {
      return (0, either_1.left)(createBooksInteriorPrintableResult.value);
    }
    interiorGroup.printables.push(createBooksInteriorPrintableResult.value);
  }
  // If there isn't a cover group - create one.
  // NOTE! Creating the cover printable group is potentially dependent on the number of interior pages,
  // so the cover group needs to be created after the interior group
  if (!coverGroup) {
    const coverPrintableGroupId = (0, uuid_1.v5)(`Time ${creationDate} Printable Group cover`, projectId);
    const printableId = (0, uuid_1.v5)(`Time ${creationDate} Printable cover`, coverPrintableGroupId);
    const createBooksCoverPrintableResult = (0, createBooksCoverPrintable_1.default)(userId, projectId, creationDate, template, hammerCategory, (0, project_1.getBookInteriorPageCount)([interiorGroup], hammerCategory), printableId);
    if (createBooksCoverPrintableResult.isLeft()) {
      return (0, either_1.left)(createBooksCoverPrintableResult.value);
    }
    coverGroup = {
      id: coverPrintableGroupId,
      type: 'cover',
      qty: 1,
      printables: [createBooksCoverPrintableResult.value]
    };
  }
  if (!hasBoxPrintableGroup || !template.woodenBox) {
    return (0, either_1.right)([coverGroup, interiorGroup]);
  }
  if (!woodenBoxGroup) {
    // If there isn't a wooden box group we'll create one.
    const woodenBoxPrintableGroupId = (0, uuid_1.v5)(`Time ${creationDate} Printable Group box`, projectId);
    const printableId = (0, uuid_1.v5)(`Time ${creationDate} Printable box`, woodenBoxPrintableGroupId);
    const createBooksBoxPrintableResult = (0, createBooksBoxPrintable_1.default)(userId, projectId, creationDate, template, printableId);
    if (createBooksBoxPrintableResult.isLeft()) {
      return (0, either_1.left)(createBooksBoxPrintableResult.value);
    }
    woodenBoxGroup = {
      id: woodenBoxPrintableGroupId,
      type: 'wooden-box',
      qty: 1,
      printables: [createBooksBoxPrintableResult.value]
    };
  }
  return (0, either_1.right)([coverGroup, interiorGroup, woodenBoxGroup]);
};
exports.createBooksPrintableGroups = createBooksPrintableGroups;
const getHardcoverDesignFilter = (template, isFirstPage) => {
  const {
    size
  } = template;
  const aspectRatio = Math.round(size.width) / Math.round(size.height);
  if (isFirstPage) {
    switch (true) {
      case aspectRatio === 1:
        return design => design.id === 'large_square';
      case aspectRatio > 1:
        return design => design.id === 'large_landscape';
      case aspectRatio < 1:
        return design => design.id === 'large_portrait';
      default:
        return design => hardcoverDesigns.includes(design.id);
    }
  }
  return design => hardcoverDesigns.includes(design.id);
};
const hardcoverDesigns = ['quarters', 'bordered', '2_large_landscape_vertical', 'full_bleed', 'landscape_2_portrait', 'large_landscape', '2_portrait_landscape', 'xlarge_portrait', 'large_portrait', '3_landscape_bordered', '2_large_portrait', '3_portrait_horizontal', '2_portrait_horizontal', 'landscape_2_portrait', '4_square', 'bordered_portrait', '2_portrait_bordered', 'landscape_portrait_horizontal', 'landscape_vertical', 'portrait', 'large_square'];